import {
  getFoundBlocks,
  getPoolHashrateChart,
  getPoolMetrics,
  getUnregUserAccountHashrateChart,
  getUnregUserAccountHashrateHistory,
  getUnregUserAccountOverview,
  getUnregUserPaymentHistory,
  getUnregUserRewardHistory,
  getUnregUserWorkers,
  getUnregUserWorkersStats,
  getUnregWorkerHashrateHistory,
  getUsers,
  getWorkerHashrate,
  getWorkerOverview,
  searchUsers
} from '@/api/stats'
import { extractData } from '@/lib/vue-query'
import { useCurrencyStore } from '@/stores/currencyStore'
import {
  FoundBlocksPaginationSchema,
  HashrateHistorySchema,
  PaymentHistorySchema,
  PoolHashrateChartSchema,
  PoolMetricsSchema,
  RewardHistorySchema,
  UnregAccountOverviewSchema,
  UnregWorkersListSchema,
  UsersListResponseSchema,
  UsersSearchResponseSchema,
  WorkerHashrateHistorySchema,
  WorkerOverviewSchema,
  WorkersHashrateSchema,
  WorkersStatsSchema
} from '@/types/api'
import {
  QUERY_CONFIGS,
  createSmartRetry,
  createQueryKey,
  createReactiveParams,
  useQueryPerformance
} from '@/lib/query-helpers'
import { extractReactiveValue, type MaybeReactive } from '@/utils/reactive-helpers'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'


/**
 * Statistics and pool data queries
 *




 */

const statsRetry = createSmartRetry(2, [2000, 3000, 4001])

/**
 * Pool metrics query
 */
export const usePoolMetricsQuery = (
  coin?: MaybeReactive<string>,
  options?: { enabled?: ComputedRef<boolean> }
) => {
  const currencyStore = useCurrencyStore()
  
  // Use provided coin (reactive or static) or fall back to centralized currency store
  const effectiveCoin = computed(() => {
    const provided = coin !== undefined ? extractReactiveValue(coin) : undefined
    const storeCoin = extractReactiveValue(currencyStore.selectedCurrency)
    return provided || storeCoin
  })
  
  const queryKey = computed(() => createQueryKey.pool.metrics(effectiveCoin.value))
  const performance = useQueryPerformance(queryKey.value)

  return useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getPoolMetrics({ coin: effectiveCoin.value })
      const data = extractData(response, "PoolMetricsQuery", "getPoolMetrics")
      performance.trackDuration()
      return PoolMetricsSchema.parse(data)
    },
    enabled: options?.enabled,
    ...QUERY_CONFIGS.REAL_TIME,
    retry: statsRetry
  })
}

/**
 * Pool hashrate chart query
 * chart_type: 1 (hourly), 2 (daily), 3 (hourly 7 days), 4 (daily month), 5 (daily year)
 */
export const usePoolHashrateChartQuery = (
  params: {
    chart_type: MaybeReactive<1 | 2 | 3 | 4 | 5>
    period?: number
    coin?: string
  },
  options?: { enabled?: ComputedRef<boolean> }
) => {
  const reactiveParams = createReactiveParams({
    chart_type: params.chart_type
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.pool.hashrateChart({
        chart_type: reactiveParams.value.chart_type,
        period: params.period,
        coin: params.coin
      })
    ),
    queryFn: async () => {
      const response = await getPoolHashrateChart({
        chart_type: reactiveParams.value.chart_type,
        period: params.period,
        coin: params.coin
      })
      const data = extractData(response, "PoolHashrateChartQuery", "getPoolHashrateChart")
      return PoolHashrateChartSchema.parse(data)
    },
    enabled: options?.enabled,
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Found blocks query
 */
export const useFoundBlocksQuery = (params?: { 
  coin?: MaybeReactive<string>
  page?: MaybeReactive<number>
  limit?: MaybeReactive<number>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    coin: params?.coin,
    page: params?.page,
    limit: params?.limit,
    sort_field: params?.sort_field,
    sort_order: params?.sort_order
  })

  return useQuery({
    queryKey: computed(() => createQueryKey.pool.foundBlocks(reactiveParams.value)),
    queryFn: async () => {
      const response = await getFoundBlocks(reactiveParams.value)
      const data = extractData(response, "FoundBlocksQuery", "getFoundBlocks")
      return FoundBlocksPaginationSchema.parse(data)
    },
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Users list query
 */
export const useUsersQuery = (params?: { 
  page?: MaybeReactive<number>
  limit?: MaybeReactive<number>
  coin?: MaybeReactive<string>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    page: params?.page,
    limit: params?.limit,
    coin: params?.coin,
    sort_field: params?.sort_field,
    sort_order: params?.sort_order
  })

  return useQuery({
    queryKey: computed(() => createQueryKey.users.list(reactiveParams.value)),
    queryFn: async () => {
      const response = await getUsers(reactiveParams.value)
      const raw = extractData(response, "UsersListQuery", "getUsers") as any

      // The API may return an array under `users` instead of `data`, and use `rank` instead of `num`.
      const items = Array.isArray(raw?.users)
        ? raw.users.map((u: any) => ({
            hashrate: u.hashrate ?? 0,
            hashrate_percent: u.hashrate_percent ?? null,
            name: u.name ?? '',
            num: u.num ?? u.rank ?? 0,
            shares_percent: u.shares_percent ?? null
          }))
        : Array.isArray(raw?.data)
        ? raw.data
        : []

      const transformed = {
        data: items,
        page_num: raw.page_num ?? raw.page ?? 1,
        page_size: raw.page_size ?? raw.limit ?? 10,
        total_page: raw.total_page ?? raw.pages ?? 1,
        total_record: raw.total_record ?? raw.total ?? items.length
      }

      return UsersListResponseSchema.parse(transformed)
    },
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Search users query
 */
export const useSearchUsersQuery = (params: { 
  query: MaybeReactive<string>
  coin?: MaybeReactive<string> 
}) => {
  const reactiveParams = createReactiveParams(params)

  return useQuery({
    queryKey: computed(() => 
      createQueryKey.users.search(reactiveParams.value.query, reactiveParams.value.coin)
    ),
    queryFn: async () => {
      if (!reactiveParams.value.query || reactiveParams.value.query.length < 2) {
        return { users: [], page: 1, pages: 1, limit: 15, total: 0 }
      }

      const response = await searchUsers(reactiveParams.value)
      const data = extractData(response) as any
      
      const transformedData = {
        users: data.users || [],
        page: data.page_num || 1,
        pages: data.total_page || 1,
        limit: data.page_size || 15,
        total: data.total_record || 0
      }
      
      return UsersSearchResponseSchema.parse(transformedData)
    },
    enabled: computed(() => !!reactiveParams.value.query && reactiveParams.value.query.length >= 2),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

// ===== UNREG USER QUERIES =====

/**
 * Unreg user account overview
 */
export const useUnregUserAccountOverviewQuery = (params: {
  user: MaybeReactive<string>
  coin?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams(params)

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.unreg.overview(reactiveParams.value.user, reactiveParams.value.coin)
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserAccountOverview(reactiveParams.value)
      const data = extractData(response, "UnregAccountOverviewQuery", "getUnregUserAccountOverview")
      return UnregAccountOverviewSchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user && reactiveParams.value.user.length > 0),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Unreg user hashrate history
 */
export const useUnregUserAccountHashrateHistoryQuery = (params: {
  user: string
  start_date?: string
  end_date?: string
  page?: number
  limit?: number
  coin?: string
}) => {
  return useQuery({
    queryKey: createQueryKey.unreg.hashrateHistory(params),
    queryFn: async () => {
      if (!params.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserAccountHashrateHistory(params)
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return HashrateHistorySchema.parse(data)
    },
    enabled: computed(() => !!params.user),
    ...QUERY_CONFIGS.HISTORICAL,
    retry: statsRetry
  })
}

/**
 * Unreg user workers list
 */
export const useUnregUserWorkersQuery = (params: { user: string; coin?: string }) => {
  return useQuery({
    queryKey: createQueryKey.unreg.workers(params.user, params.coin),
    queryFn: async () => {
      if (!params.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserWorkers(params)
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return UnregWorkersListSchema.parse(data)
    },
    enabled: computed(() => !!params.user),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Worker overview query
 */
export const useWorkerOverviewQuery = (params: {
  user: MaybeReactive<string>
  worker: MaybeReactive<string>
  coin?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams(params)

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.worker.overview(
        reactiveParams.value.user,
        reactiveParams.value.worker,
        reactiveParams.value.coin
      )
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user || !reactiveParams.value.worker) {
        throw new Error('User and worker are required')
      }

      const response = await getWorkerOverview(reactiveParams.value)
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return WorkerOverviewSchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user && !!reactiveParams.value.worker),
    ...QUERY_CONFIGS.REAL_TIME,
    retry: statsRetry
  })
}

/**
 * Worker hashrate query
 */
export const useWorkerHashrateQuery = (params: {
  user: MaybeReactive<string>
  page?: number
  limit?: number
  coin?: MaybeReactive<string>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    user: params.user,
    coin: params.coin,
    sort_field: params.sort_field,
    sort_order: params.sort_order
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.worker.hashrate({
        ...reactiveParams.value,
        page: params.page,
        limit: params.limit
      })
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User is required')
      }

      const response = await getWorkerHashrate({
        user: reactiveParams.value.user,
        page: params.page,
        limit: params.limit,
        coin: reactiveParams.value.coin,
        sort_field: reactiveParams.value.sort_field,
        sort_order: reactiveParams.value.sort_order
      })
      const data = extractData(response, 'WorkerHashrateQuery', 'getWorkerHashrate')
      return WorkersHashrateSchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user && reactiveParams.value.user.length > 0),
    ...QUERY_CONFIGS.REAL_TIME,
    retry: statsRetry
  })
}

/**
 * Unreg worker hashrate history
 */
export const useUnregWorkerHashrateHistoryQuery = (params: {
  user: MaybeReactive<string>
  worker: MaybeReactive<string>
  chart_type: MaybeReactive<1 | 2 | 3 | 4 | 5>
  period?: MaybeReactive<number>
  coin?: MaybeReactive<string>
}) => {
  const reactiveParams = createReactiveParams({
    user: params.user,
    worker: params.worker,
    chart_type: params.chart_type,
    period: params.period,
    coin: params.coin
  })

  return useQuery({
    queryKey: computed(() => createQueryKey.worker.hashrateHistory({
      ...reactiveParams.value
    })),
    queryFn: async () => {
      if (!reactiveParams.value.user || !reactiveParams.value.worker) {
        throw new Error('User and worker are required')
      }

      const response = await getUnregWorkerHashrateHistory({
        user: reactiveParams.value.user,
        worker: reactiveParams.value.worker,
        chart_type: reactiveParams.value.chart_type,
        period: reactiveParams.value.period,
        coin: reactiveParams.value.coin
      })
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return WorkerHashrateHistorySchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user && !!reactiveParams.value.worker),
    ...QUERY_CONFIGS.HISTORICAL,
    retry: statsRetry
  })
}

/**
 * Unreg user payment history
 */
export const useUnregUserPaymentHistoryQuery = (params: {
  user: MaybeReactive<string>
  start_date?: MaybeReactive<string | undefined>
  end_date?: MaybeReactive<string | undefined>
  range?: MaybeReactive<'week' | 'month' | 'year' | undefined>
  page?: number
  limit?: number
  coin?: MaybeReactive<string>
  worker?: MaybeReactive<string | undefined>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<'asc' | 'desc'>
}) => {
  const reactiveParams = createReactiveParams({
    user: params.user,
    coin: params.coin,
    worker: params.worker,
    start_date: params.start_date,
    end_date: params.end_date,
    range: params.range,
    sort_field: params.sort_field,
    sort_order: params.sort_order
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.unreg.paymentHistory({
        ...reactiveParams.value,
        page: params.page,
        limit: params.limit
      })
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserPaymentHistory({
        user: reactiveParams.value.user,
        start_date: reactiveParams.value.start_date,
        end_date: reactiveParams.value.end_date,
        range: reactiveParams.value.range,
        page: params.page,
        limit: params.limit,
        coin: reactiveParams.value.coin,
        worker: reactiveParams.value.worker,
        sort_field: reactiveParams.value.sort_field,
        sort_order: reactiveParams.value.sort_order
      })
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return PaymentHistorySchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Unreg user account hashrate chart
 * chart_type: 1 (hourly), 2 (daily), 3 (hourly 7 days), 4 (daily month), 5 (daily year)
 */
export const useUnregUserAccountHashrateChartQuery = (
  params: {
    user: MaybeReactive<string>
    chart_type: MaybeReactive<1 | 2 | 3 | 4 | 5>
    period?: number
    coin?: MaybeReactive<string>
  },
  options?: { enabled?: ComputedRef<boolean> }
) => {
  const reactiveParams = createReactiveParams({
    user: params.user,
    chart_type: params.chart_type,
    coin: params.coin
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.unreg.hashrateChart({
        ...reactiveParams.value,
        period: params.period
      })
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserAccountHashrateChart({
        user: reactiveParams.value.user,
        chart_type: reactiveParams.value.chart_type,
        period: params.period,
        coin: reactiveParams.value.coin
      })
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return PoolHashrateChartSchema.parse(data)
    },
    enabled:
      options?.enabled ??
      computed(() => !!reactiveParams.value.user && reactiveParams.value.user.length > 0),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Unreg user reward history
 */
export const useUnregUserRewardHistoryQuery = (params: {
  user: MaybeReactive<string>
  start_date?: MaybeReactive<string | undefined>
  end_date?: MaybeReactive<string | undefined>
  range?: MaybeReactive<'week' | 'month' | 'year' | undefined>
  page?: number
  limit?: number
  coin?: MaybeReactive<string>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<'asc' | 'desc'>
  worker?: MaybeReactive<string | undefined>
}) => {
  const reactiveParams = createReactiveParams({
    user: params.user,
    coin: params.coin,
    worker: params.worker,
    start_date: params.start_date,
    end_date: params.end_date,
    range: params.range,
    sort_field: params.sort_field,
    sort_order: params.sort_order
  })

  return useQuery({
    queryKey: computed(() =>
      createQueryKey.unreg.rewardHistory({
        ...reactiveParams.value,
        page: params.page,
        limit: params.limit,
        sort_field: params.sort_field,
        sort_order: params.sort_order
      })
    ),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User is required')
      }

      const response = await getUnregUserRewardHistory({
        user: reactiveParams.value.user,
        start_date: reactiveParams.value.start_date,
        end_date: reactiveParams.value.end_date,
        range: reactiveParams.value.range,
        page: params.page,
        limit: params.limit,
        coin: reactiveParams.value.coin,
        sort_field: reactiveParams.value.sort_field,
        sort_order: reactiveParams.value.sort_order,
        worker: reactiveParams.value.worker
      })
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return RewardHistorySchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user),
    ...QUERY_CONFIGS.FREQUENT,
    retry: statsRetry
  })
}

/**
 * Unreg user workers stats
 */
export const useUnregUserWorkersStatsQuery = (params: {
  user: MaybeReactive<string>
  page?: MaybeReactive<number>
  limit?: MaybeReactive<number>
  coin?: MaybeReactive<string>
  status?: MaybeReactive<string | undefined>
  search?: MaybeReactive<string | undefined>
  sort_field?: MaybeReactive<string>
  sort_order?: MaybeReactive<string>
}) => {

  const reactiveParams = createReactiveParams({
    user: params.user,
    page: params.page || 1,
    limit: params.limit || 50,
    coin: params.coin || 'BTC',
    status: params.status,
    search: params.search,
    sort_field: params.sort_field || 'last_share',
    sort_order: params.sort_order || 'desc'
  })

  return useQuery({
    queryKey: computed(() => createQueryKey.unreg.workerStats(reactiveParams.value)),
    queryFn: async () => {
      if (!reactiveParams.value.user) {
        throw new Error('User parameter is required')
      }

      const response = await getUnregUserWorkersStats(reactiveParams.value)
      const data = extractData(response, "UnregStatsQuery", "getStatsData")
      return WorkersStatsSchema.parse(data)
    },
    enabled: computed(() => !!reactiveParams.value.user),
    ...QUERY_CONFIGS.REAL_TIME,
    retry: statsRetry
  })
}
